import type { TelemetrySample, AlarmEvent } from '../../store/Database';
import type { AppState } from '../../types/state';
import { ReportGenerator } from './ReportGenerator';

export type ExportFormat = 'csv' | 'json' | 'pdf';

export interface ReportData {
  generatedAt: string;
  device: string;
  samples: TelemetrySample[];
  alarms: AlarmEvent[];
  state: Partial<AppState>;
}

export class ExportService {
  static toCSV(samples: TelemetrySample[]): string {
    const headers = [
      'Timestamp',
      'Frequencia (Hz)',
      'Corrente (A)',
      'Tensao CC (V)',
      'Temperatura (°C)',
      'Carga (%)',
    ];

    const rows = samples.map((s) =>
      [
        s.timestamp.toISOString(),
        s.frequency.toFixed(2),
        s.current.toFixed(2),
        s.dcVoltage.toString(),
        s.temperature.toFixed(1),
        s.load.toString(),
      ].join(','),
    );

    return '\uFEFF' + headers.join(',') + '\n' + rows.join('\n');
  }

  static toJSON(report: ReportData): string {
    return JSON.stringify(report, null, 2);
  }

  static download(content: string, filename: string, mime: string): void {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  static downloadCSV(samples: TelemetrySample[], filename?: string): void {
    const csv = this.toCSV(samples);
    this.download(
      csv,
      filename ?? `prevdrive-export-${Date.now()}.csv`,
      'text/csv;charset=utf-8;',
    );
  }

  static downloadJSON(report: ReportData, filename?: string): void {
    const json = this.toJSON(report);
    this.download(
      json,
      filename ?? `prevdrive-report-${Date.now()}.json`,
      'application/json',
    );
  }

  static async downloadReport(
    state: AppState,
    samples: TelemetrySample[],
    alarms: AlarmEvent[],
  ): Promise<void> {
    await ReportGenerator.generate({ state, samples, alarms });
  }

  static async downloadQuickReport(report: ReportData): Promise<void> {
    const lines = [
      `Relatório PREVDRIVE - ${report.generatedAt}`,
      `Dispositivo: ${report.device}`,
      '',
      'Últimos parâmetros:',
      `  Frequência: ${report.state.currentFreq?.toFixed(1) ?? '-'} Hz`,
      `  Corrente: ${report.state.currentAmp?.toFixed(1) ?? '-'} A`,
      `  Tensão CC: ${report.state.dcVoltage ?? '-'} V`,
      `  Temperatura: ${report.state.heatsinkTemp?.toFixed(1) ?? '-'} °C`,
      '',
      `Alarmes registrados: ${report.alarms.length}`,
      `Amostras coletadas: ${report.samples.length}`,
    ];

    const text = lines.join('\n');
    this.download(text, `prevdrive-quick-report-${Date.now()}.txt`, 'text/plain');
  }
}
